import qrcode from "qrcode-generator";

// The code as a camera reads it: dark on light whatever the page's theme is, that being the one contrast the format
// is defined in and the only one every reader is sure of. The white is `.qr-code`'s own rather than the theme's, so
// the quiet zone the viewBox leaves is part of the code and not a margin whatever is holding it happened to have.
export function QrCode({ modules, label }: QrCodeProps) {
  const span = modules.length + QR_QUIET_ZONE * 2;

  return (
    <div className="qr-code">
      <svg viewBox={`0 0 ${span} ${span}`} role="img" aria-label={label} shapeRendering="crispEdges">
        <path d={qrPath(modules)} fill="#000" />
      </svg>
    </div>
  );
}

interface QrCodeProps {
  modules: boolean[][];
  label: string;
}

// The redundancy the format defines, from the seven per cent a code can lose at L to the thirty it can at H.
export type QrCorrection = "L" | "M" | "Q" | "H";

// A QR code carries bytes and the library reads a string for them a character at a time, keeping the low eight bits
// of each: Latin-1, which is every URI /otp writes and not every one somebody can type into a box. UTF-8 is what a
// reader takes those bytes for, so it is what they are made of.
qrcode.stringToBytes = (text: string) => Array.from(new TextEncoder().encode(text));

// The four empty modules the format asks for around a code, without which a reader has no edge to find it by.
export const QR_QUIET_ZONE = 4;

// The rows of a code, dark first. The encoding is the library's and the drawing is `QrCode`'s, which is what lets the
// code be an SVG sized to whatever is holding it rather than the markup the library writes for itself. Null is text
// no version has room for at this level of correction, which is a code nobody could have scanned anyway.
export function qrModules(text: string, correction: QrCorrection = "M"): boolean[][] | null {
  try {
    // Type 0 is the smallest version the text fits; M is the default because it is what an authenticator's own uses.
    const code = qrcode(0, correction);
    code.addData(text);
    code.make();
    const size = code.getModuleCount();
    return Array.from({ length: size }, (_, row) => Array.from({ length: size }, (_, col) => code.isDark(row, col)));
  } catch {
    return null;
  }
}

// One path for the whole code rather than a rect per module, which for a version 6 code is two thousand nodes the
// browser would be laying out. A run of dark modules in a row is one subpath, and the quiet zone is the offset.
export function qrPath(modules: boolean[][]): string {
  const parts: string[] = [];
  for (const [row, cells] of modules.entries()) {
    let start = -1;
    for (let col = 0; col <= cells.length; col++) {
      if (cells[col]) {
        if (start === -1) start = col;
        continue;
      }
      if (start === -1) continue;
      parts.push(`M${start + QR_QUIET_ZONE} ${row + QR_QUIET_ZONE}h${col - start}v1h-${col - start}z`);
      start = -1;
    }
  }
  return parts.join("");
}
